import { packTotalCents, type CatalogFacets, type ListedPiece } from "./catalog-types";
import { formatBrlInteger } from "./money";

/**
 * Catalog facets (handoff §7.3, criteria 17.8, 17.9 and 17.11).
 *
 * The relation is **OR within a facet and AND across facets**, which is what makes
 * refinement narrow instead of accumulate. "Destaques" sits beside the facets in the rail
 * but is not one: it is a boolean toggle with no option list (Desvio 10 in the design).
 */

export type FacetKey = "tipo" | "colecao" | "material" | "preco";

export const FACETS: readonly { key: FacetKey; label: string }[] = [
  // First since v2, where the type left the taxonomy track to become an attribute.
  { key: "tipo", label: "Tipo" },
  { key: "colecao", label: "Coleção" },
  { key: "material", label: "Material" },
  { key: "preco", label: "Faixa de preço" },
];

/** Option count above which a facet grows its own search field (handoff §7.3). */
export const FACET_SEARCH_THRESHOLD = 8;

/**
 * Selections, as a set of `facet:value` keys.
 *
 * A composite key rather than a map per facet: toggling is one operation and counting a
 * facet's selections is a prefix filter, where a map would create and drop empty arrays on
 * every tap.
 */
export type FacetSelection = ReadonlySet<string>;

export const EMPTY_FACET_SELECTION: FacetSelection = new Set<string>();

export function facetOptionKey(facet: FacetKey, value: string): string {
  return `${facet}:${value}`;
}

export function toggleFacetOption(
  selection: FacetSelection,
  facet: FacetKey,
  value: string,
): Set<string> {
  const next = new Set(selection);
  const key = facetOptionKey(facet, value);

  if (next.has(key)) next.delete(key);
  else next.add(key);

  return next;
}

export function selectedValues(selection: FacetSelection, facet: FacetKey): string[] {
  const prefix = `${facet}:`;

  return [...selection]
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
}

export function selectedCount(selection: FacetSelection, facet: FacetKey): number {
  return selectedValues(selection, facet).length;
}

/** Price range in cents. Null on either end means "no bound". */
export type PriceRange = {
  minCents: number | null;
  maxCents: number | null;
};

export const EMPTY_PRICE_RANGE: PriceRange = { minCents: null, maxCents: null };

export function hasPriceRange(price: PriceRange): boolean {
  return price.minCents !== null || price.maxCents !== null;
}

/** A piece's faceted value; type, collection and material are distinct fields. */
function facetValueOf(piece: ListedPiece, facet: Exclude<FacetKey, "preco">): string {
  if (facet === "tipo") return piece.type;

  return facet === "colecao" ? piece.collection : piece.material;
}

export type FacetOption = {
  value: string;
  label: string;
  count: number;
  isSelected: boolean;
};

/** §5: up to twenty values in alphabetical order; above, by volume, then by name. */
function sortOptions(options: FacetOption[]): FacetOption[] {
  return options.length > ALPHABETICAL_LIMIT
    ? options.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "pt-BR"))
    : options.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

/** A facet's options, counted over the given slice (§5's order, as the server's). */
export function facetOptions(
  pieces: readonly ListedPiece[],
  facet: Exclude<FacetKey, "preco">,
  selection: FacetSelection,
): FacetOption[] {
  const tally = new Map<string, number>();

  for (const piece of pieces) {
    const value = facetValueOf(piece, facet);
    if (value.length === 0) continue;
    tally.set(value, (tally.get(value) ?? 0) + 1);
  }

  return sortOptions(
    [...tally.entries()].map(([value, count]) => ({
      value,
      label: value,
      count,
      isSelected: selection.has(facetOptionKey(facet, value)),
    })),
  );
}

/**
 * Whether a piece passes one facet.
 *
 * An unmarked facet excludes nobody, and on price **an absent bound excludes nothing**
 * (criterion 17.11): treating a missing bound as zero would hide the whole catalog on the
 * first tap.
 */
export function passesFacet(
  piece: ListedPiece,
  facet: FacetKey,
  selection: FacetSelection,
  price: PriceRange,
): boolean {
  if (facet === "preco") {
    if (!hasPriceRange(price)) return true;

    const total = packTotalCents(piece);

    return (
      total >= (price.minCents ?? Number.NEGATIVE_INFINITY) &&
      total <= (price.maxCents ?? Number.POSITIVE_INFINITY)
    );
  }

  const targets = selectedValues(selection, facet);

  return targets.length === 0 || targets.includes(facetValueOf(piece, facet));
}

export type FacetFilters = {
  selection: FacetSelection;
  price: PriceRange;
  onlyFeatured: boolean;
};

export const EMPTY_FACET_FILTERS: FacetFilters = {
  selection: EMPTY_FACET_SELECTION,
  price: EMPTY_PRICE_RANGE,
  onlyFeatured: false,
};

export function applyFacets(
  pieces: readonly ListedPiece[],
  filters: FacetFilters,
): ListedPiece[] {
  return pieces.filter(
    (piece) =>
      FACETS.every((facet) => passesFacet(piece, facet.key, filters.selection, filters.price)) &&
      (!filters.onlyFeatured || piece.featured),
  );
}

/**
 * Whether any facet filter is active.
 *
 * Taxonomy is deliberately excluded: "Limpar filtros" clears marks, featured and price and
 * leaves the track alone, because the track is navigation and a facet is refinement.
 */
export function hasActiveFacets(filters: FacetFilters): boolean {
  return filters.selection.size > 0 || filters.onlyFeatured || hasPriceRange(filters.price);
}

/** Range summary, with the prototype's literal copy when unbounded. */
export function priceSummary(price: PriceRange): string {
  if (!hasPriceRange(price)) return "toda a faixa";

  return `R$ ${formatBrlInteger(price.minCents ?? 0)} a R$ ${formatBrlInteger(price.maxCents ?? 0)}`;
}

/** Placeholder for a facet's own search field. */
export function facetSearchPlaceholder(facet: FacetKey): string {
  const label = FACETS.find((entry) => entry.key === facet)?.label ?? "opção";

  return `Buscar ${label.toLowerCase()}`;
}

/** Filters options by the facet's search term. */
export function searchFacetOptions(options: readonly FacetOption[], term: string): FacetOption[] {
  const query = term.trim().toLowerCase();

  if (query.length === 0) return [...options];

  return options.filter((option) => option.label.toLowerCase().includes(query));
}

/**
 * The server's options for one facet, in the rail's own shape (filters spec §5).
 *
 * Alphabetical up to twenty values; above that, by volume — with 93 collections in staging,
 * alphabetical order buries what the catalog actually holds.
 */
export function serverOptions(
  facets: CatalogFacets,
  facet: Exclude<FacetKey, "preco">,
  selection: FacetSelection,
): FacetOption[] {
  const source =
    facet === "tipo" ? facets.types : facet === "colecao" ? facets.collections : facets.materials;

  const options = source.map((option) => ({
    value: option.value,
    label: option.label,
    count: option.count,
    isSelected: selection.has(facetOptionKey(facet, option.value)),
  }));

  return sortOptions(options);
}

/** §5: "Até 20 valores, ordem alfabética; acima, por volume decrescente." */
export const ALPHABETICAL_LIMIT = 20;

/**
 * The marks an address carries, as the rail holds them (`catalog-query.ts` is the other side).
 *
 * A filtered address has to open filtered: the page the server rendered answers that same
 * query, and the chips have to agree with the grid from the first paint.
 */
export function facetsFromCatalogQuery(query: {
  collectionId: string | null;
  types: readonly string[];
  materials: readonly string[];
  priceMinCents: number | null;
  priceMaxCents: number | null;
}): FacetFilters {
  const selection = new Set<string>();

  if (query.collectionId !== null) selection.add(facetOptionKey("colecao", query.collectionId));
  for (const type of query.types) selection.add(facetOptionKey("tipo", type));
  for (const material of query.materials) selection.add(facetOptionKey("material", material));

  return {
    selection,
    price: { minCents: query.priceMinCents, maxCents: query.priceMaxCents },
    onlyFeatured: false,
  };
}

/**
 * One collection at a time.
 *
 * The rail marks like every other facet — OR within the axis — but `GET /catalog/products` takes
 * a single `collectionId`, so a second mark would travel nowhere and the chip would count a
 * slice the grid never shows. The newest mark wins, which is what a person means by tapping it.
 */
export function withSingleCollection(previous: FacetFilters, next: FacetFilters): FacetFilters {
  const collections = selectedValues(next.selection, "colecao");
  if (collections.length <= 1) return next;

  const kept = collections.find((value) => !selectedValues(previous.selection, "colecao").includes(value));
  const selection = new Set(
    [...next.selection].filter((key) => !key.startsWith("colecao:") || key === facetOptionKey("colecao", kept ?? collections[0])),
  );

  return { ...next, selection };
}
